import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Edit, Trash2, Star, Loader2, Wand2, Upload, FileText } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = {
  title: "", type: "domestic", destination: "", short_description: "",
  description: "", price: "", price_label: "per person", duration: "",
  image_url: "", highlights: "", inclusions: "", exclusions: "",
  is_featured: false, is_active: true,
};

export default function PackagesManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [aiDialog, setAiDialog] = useState(false);
  const [aiForm, setAiForm] = useState({ file: null, context: "", loading: false, clarificationMsg: "" });

  const load = () => { setLoading(true); api.listPackages().then((d) => setItems(d.items || [])).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(load, []);

  const openNew = () => setEditing({ ...EMPTY });
  const openEdit = (p) => setEditing({
    ...p,
    price: p.price ?? "",
    highlights: (p.highlights || []).join("\n"),
    inclusions: (p.inclusions || []).join("\n"),
    exclusions: (p.exclusions || []).join("\n"),
  });

  const save = async () => {
    if (!editing.title || !editing.type) return toast.error("Title & type required");
    setSaving(true);
    try {
      const payload = {
        ...editing,
        price: editing.price !== "" ? Number(editing.price) : null,
        highlights: editing.highlights ? editing.highlights.split("\n").filter(Boolean) : [],
        inclusions: editing.inclusions ? editing.inclusions.split("\n").filter(Boolean) : [],
        exclusions: editing.exclusions ? editing.exclusions.split("\n").filter(Boolean) : [],
      };
      if (editing.id) await api.updatePackage(editing.id, payload);
      else await api.createPackage(payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!confirm("Delete this package?")) return;
    await api.deletePackage(id);
    toast.success("Deleted");
    load();
  };

  const handleGenerate = async () => {
    if (!aiForm.file && !aiForm.context.trim()) {
      return toast.error("Please provide a file or text description");
    }
    setAiForm(p => ({ ...p, loading: true, clarificationMsg: "" }));
    try {
      const res = await api.generatePackage(aiForm.file, aiForm.context);
      if (res.package?.clarification_needed) {
        setAiForm(p => ({ ...p, clarificationMsg: res.package.clarification_message, loading: false }));
        return;
      }
      
      const p = res.package;
      // Pre-fill the form with AI data
      setEditing({
        ...EMPTY,
        title: p.title || "",
        type: p.type || "domestic",
        destination: p.destination || "",
        short_description: p.short_description || "",
        description: p.description || "",
        price: p.price || "",
        price_label: p.price_label || "per person",
        duration: p.duration || "",
        image_url: p.image_url || "",
        highlights: (p.highlights || []).join("\n"),
        inclusions: (p.inclusions || []).join("\n"),
        exclusions: (p.exclusions || []).join("\n")
      });
      setAiDialog(false);
      setAiForm({ file: null, context: "", loading: false, clarificationMsg: "" });
      toast.success("AI generated the package! Please review before saving.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate package. File might be too large or AI error.");
      setAiForm(p => ({ ...p, loading: false }));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold text-slate-800">Tour Packages</h1>
          <p className="text-slate-500">Manage packages shown on your public website.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setAiDialog(true)} className="rounded-xl bg-purple-600 hover:bg-purple-700 text-white shadow-[0_0_15px_rgba(147,51,234,0.3)]">
            <Wand2 className="w-4 h-4 mr-2" /> AI Generate
          </Button>
          <Button onClick={openNew} className="rounded-xl btn-luxe " data-testid="packages-new-button">
            <Plus className="w-4 h-4 mr-2" /> New Package
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-5 h-5 animate-spin text-slate-800" /></div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center rounded-2xl glass-panel-dark border-slate-200">
          <div className="font-display font-semibold text-slate-800">No packages yet</div>
          <p className="text-slate-500 mt-1">Add your first tour package.</p>
          <Button onClick={openNew} className="mt-3 rounded-xl btn-luxe ">Add Package</Button>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p) => (
            <Card key={p.id} className="rounded-2xl overflow-hidden glass-panel-dark border-slate-200">
              <div className="relative h-40">
                <img src={p.image_url || "https://images.pexels.com/photos/2387873/pexels-photo-2387873.jpeg"} className="w-full h-full object-cover" alt="" />
                <div className="absolute top-2 right-2 flex gap-1">
                  {p.is_featured && <Badge className="bg-yellow-500/20 text-yellow-400 border border-yellow-500/50 hover:bg-yellow-500/30"><Star className="w-3 h-3 mr-1" />Featured</Badge>}
                  <Badge className="bg-[#ea580c] text-white border-none hover:bg-[#c2410c] shadow-md uppercase tracking-wider text-[10px] font-bold">{p.type}</Badge>
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-display font-semibold text-slate-800">{p.title}</h3>
                <p className="text-xs text-slate-500">{p.destination} · {p.duration}</p>
                <p className="text-sm text-orange-500 font-semibold mt-1">{p.price ? `₹${Number(p.price).toLocaleString("en-IN")}` : "On Request"}</p>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => openEdit(p)} className="rounded-lg border-slate-200 text-slate-800 hover:bg-slate-100 hover:text-white" data-testid={`pkg-edit-${p.id}`}><Edit className="w-3 h-3 mr-1" /> Edit</Button>
                  <Button size="sm" variant="outline" onClick={() => del(p.id)} className="rounded-lg border-slate-200 text-red-400 hover:bg-slate-100 hover:text-red-300"><Trash2 className="w-3 h-3 mr-1" /> Delete</Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto glass-panel-dark border-slate-200 text-slate-800">
          <DialogHeader><DialogTitle className="text-slate-800">{editing?.id ? "Edit" : "New"} Package</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2"><Label className="text-slate-800">Title *</Label><Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="rounded-xl bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400" data-testid="pkg-title-input" /></div>
                <div>
                  <Label className="text-slate-800">Type *</Label>
                  <Select value={editing.type} onValueChange={(v) => setEditing({ ...editing, type: v })}>
                    <SelectTrigger className="rounded-xl bg-slate-50 border-slate-200 text-slate-800" data-testid="pkg-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 text-slate-800">
                      <SelectItem value="domestic">Domestic</SelectItem>
                      <SelectItem value="international">International</SelectItem>
                      <SelectItem value="group">Group</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-slate-800">Destination</Label><Input value={editing.destination} onChange={(e) => setEditing({ ...editing, destination: e.target.value })} className="rounded-xl bg-slate-50 border-slate-200 text-slate-800" /></div>
                <div><Label className="text-slate-800">Duration</Label><Input value={editing.duration} onChange={(e) => setEditing({ ...editing, duration: e.target.value })} placeholder="e.g., 5N/6D" className="rounded-xl bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400" /></div>
                <div><Label className="text-slate-800">Price (₹)</Label><Input type="number" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className="rounded-xl bg-slate-50 border-slate-200 text-slate-800" /></div>
                <div><Label className="text-slate-800">Price Label</Label><Input value={editing.price_label} onChange={(e) => setEditing({ ...editing, price_label: e.target.value })} className="rounded-xl bg-slate-50 border-slate-200 text-slate-800" /></div>
              </div>
              <div><Label className="text-slate-800">Image URL</Label><Input value={editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} placeholder="https://..." className="rounded-xl bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400" /></div>
              <div><Label className="text-slate-800">Short Description</Label><Textarea rows={2} value={editing.short_description} onChange={(e) => setEditing({ ...editing, short_description: e.target.value })} className="rounded-xl bg-slate-50 border-slate-200 text-slate-800" /></div>
              <div><Label className="text-slate-800">Full Description</Label><Textarea rows={4} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="rounded-xl bg-slate-50 border-slate-200 text-slate-800" /></div>
              <div><Label className="text-slate-800">Highlights (one per line)</Label><Textarea rows={3} value={editing.highlights} onChange={(e) => setEditing({ ...editing, highlights: e.target.value })} className="rounded-xl bg-slate-50 border-slate-200 text-slate-800" /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label className="text-slate-800">Inclusions (one per line)</Label><Textarea rows={3} value={editing.inclusions} onChange={(e) => setEditing({ ...editing, inclusions: e.target.value })} className="rounded-xl bg-slate-50 border-slate-200 text-slate-800" /></div>
                <div><Label className="text-slate-800">Exclusions (one per line)</Label><Textarea rows={3} value={editing.exclusions} onChange={(e) => setEditing({ ...editing, exclusions: e.target.value })} className="rounded-xl bg-slate-50 border-slate-200 text-slate-800" /></div>
              </div>
              <div className="flex gap-4 pt-2">
                <div className="flex items-center gap-2"><Switch checked={editing.is_featured} onCheckedChange={(v) => setEditing({ ...editing, is_featured: v })} id="is_featured" /><Label className="text-slate-800" htmlFor="is_featured">Featured</Label></div>
                <div className="flex items-center gap-2"><Switch checked={editing.is_active} onCheckedChange={(v) => setEditing({ ...editing, is_active: v })} id="is_active" /><Label className="text-slate-800" htmlFor="is_active">Active</Label></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="border-slate-200 text-slate-800 hover:bg-slate-100 hover:text-white">Cancel</Button>
            <Button onClick={save} disabled={saving} className="btn-luxe  text-white" data-testid="pkg-save-button">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={aiDialog} onOpenChange={setAiDialog}>
        <DialogContent className="max-w-md rounded-2xl p-0 overflow-hidden glass-panel-dark border-slate-200 text-slate-800">
          <div className="bg-gradient-to-br from-purple-600 to-indigo-700 p-6 text-slate-800 text-center">
            <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3 backdrop-blur-md">
              <Wand2 className="w-6 h-6 text-slate-800" />
            </div>
            <DialogTitle className="text-xl font-display font-bold">AI Package Generator</DialogTitle>
            <p className="text-purple-100 text-sm mt-1">Upload a vendor PDF/Image or type what you want, and AI will build the entire package!</p>
          </div>
          
          <div className="p-6 space-y-4">
            {aiForm.clarificationMsg && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 p-3 rounded-xl text-sm flex gap-2">
                <span className="font-bold">Wait!</span> {aiForm.clarificationMsg}
              </div>
            )}
            
            <div className="border-2 border-dashed border-white/20 rounded-xl p-4 text-center hover:bg-slate-50 transition-colors">
              <input
                type="file"
                id="ai-package-upload"
                className="hidden"
                accept=".pdf,image/*"
                onChange={(e) => setAiForm({ ...aiForm, file: e.target.files[0], clarificationMsg: "" })}
              />
              <Label htmlFor="ai-package-upload" className="cursor-pointer flex flex-col items-center gap-2">
                <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center text-purple-400">
                  <Upload className="w-5 h-5" />
                </div>
                <div className="text-sm font-medium text-slate-800">
                  {aiForm.file ? aiForm.file.name : "Upload Vendor PDF or Image"}
                </div>
                <div className="text-xs text-slate-500">
                  {aiForm.file ? "Click to change file" : "Max 50MB"}
                </div>
              </Label>
            </div>

            <div className="flex items-center gap-4 py-2">
              <div className="flex-1 h-px bg-slate-100"></div>
              <span className="text-xs font-semibold text-slate-500 uppercase">AND / OR</span>
              <div className="flex-1 h-px bg-slate-100"></div>
            </div>

            <div>
              <Label className="text-slate-800 font-semibold mb-2 block flex items-center gap-2">
                <FileText className="w-4 h-4 text-purple-400" />
                Describe the Package
              </Label>
              <Textarea 
                placeholder="e.g. Create a 5 Nights 6 Days premium couple package for Maldives..."
                className="rounded-xl bg-slate-50 border-slate-200 focus:bg-slate-100 text-white placeholder-slate-400 resize-none"
                rows={4}
                value={aiForm.context}
                onChange={e => setAiForm({ ...aiForm, context: e.target.value, clarificationMsg: "" })}
              />
            </div>
          </div>

          <DialogFooter className="p-4 border-t border-slate-200 bg-slate-50">
            <Button variant="ghost" onClick={() => setAiDialog(false)} className="rounded-xl text-slate-800 hover:bg-slate-100">Cancel</Button>
            <Button 
              onClick={handleGenerate} 
              disabled={aiForm.loading || (!aiForm.file && !aiForm.context.trim())}
              className="rounded-xl bg-purple-600 hover:bg-purple-700 text-white min-w-[120px]"
            >
              {aiForm.loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
              {aiForm.loading ? "Generating..." : "Generate ✨"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
